import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GraduationCap, Plus, Trash2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function Courses() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [taking, setTaking] = useState(null);
  const canManage = user.role === "admin" || user.role === "manager";
  const load = () => api.get("/courses").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!window.confirm("Delete course?")) return; await api.delete(`/courses/${id}`); load(); };
  return (
    <div className="space-y-6" data-testid="courses-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--accent)]">Training</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Courses & Quizzes</h1>
        </div>
        {canManage && <button onClick={() => setOpen(true)} data-testid="new-course" className="px-4 py-2.5 rounded-lg font-bold text-black inline-flex items-center gap-2" style={{ background: "var(--accent)" }}><Plus size={16} /> New course</button>}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {rows.map((c) => {
          const p = c.my_progress;
          return (
            <div key={c.id} data-testid={`course-${c.id}`} className="card-surface p-5">
              <div className="flex items-start justify-between gap-2">
                <GraduationCap size={22} className="text-[color:var(--accent)]" />
                {canManage && <button onClick={() => del(c.id)} className="text-slate-500 hover:text-[#ff3b5c]"><Trash2 size={14} /></button>}
              </div>
              <h3 className="text-lg font-bold mt-2">{c.title}</h3>
              {c.description && <p className="text-sm text-slate-400 mt-1">{c.description}</p>}
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-2">{c.questions?.length || 0} question(s) · pass at {c.pass_threshold}% · {c.max_attempts || 3} attempts</div>
              {(() => {
                const maxA = c.max_attempts || 3;
                const used = p?.attempts || 0;
                const left = Math.max(0, maxA - used);
                const passed = p?.status === "passed";
                const exhausted = !passed && used >= maxA;
                if (passed) return (
                  <div className="mt-3 pill border inline-flex items-center gap-1 bg-[#ff000022] text-[color:var(--accent)] border-[#ff000044]"><CheckCircle2 size={12} /> {p.score}% · Passed</div>
                );
                if (exhausted) return (
                  <div className="mt-3 pill border inline-flex items-center gap-1 bg-[#ff3b5c20] text-[#ff3b5c] border-[#ff3b5c40]" data-testid={`locked-${c.id}`}><XCircle size={12} /> Locked · {maxA}/{maxA} attempts used</div>
                );
                return (
                  <div className="mt-3 flex items-center gap-3">
                    <button onClick={() => setTaking(c)} data-testid={`take-${c.id}`} className="px-3 py-1.5 rounded-lg bg-[color:var(--accent)] text-black font-bold text-xs">{used > 0 ? "Retake quiz" : "Start course"}</button>
                    <span className="text-[10px] uppercase tracking-widest text-slate-500" data-testid={`attempts-${c.id}`}>{left} attempt{left === 1 ? "" : "s"} left{p?.score != null ? ` · last ${p.score}%` : ""}</span>
                  </div>
                );
              })()}
            </div>
          );
        })}
        {rows.length === 0 && <div className="col-span-full card-surface p-10 text-center text-slate-500"><GraduationCap size={24} className="inline mr-2" /> No courses yet.</div>}
      </div>
      {open && <NewCourse onClose={() => setOpen(false)} onDone={() => { load(); setOpen(false); }} />}
      {taking && <TakeCourse course={taking} onClose={() => setTaking(null)} onDone={() => { load(); setTaking(null); }} />}
    </div>
  );
}

function TakeCourse({ course, onClose, onDone }) {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const submit = async (e) => { e.preventDefault(); const { data } = await api.post(`/courses/${course.id}/submit`, { answers }); setResult(data); toast[data.status === "passed" ? "success" : "error"](`${data.score}% — ${data.status.toUpperCase()}`); setTimeout(onDone, 1800); };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <form onSubmit={submit} className="card-surface p-6 w-full max-w-2xl max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="course-take">
        <h2 className="text-2xl font-bold mb-2">{course.title}</h2>
        {course.content && <div className="text-slate-300 whitespace-pre-wrap text-sm mb-4">{course.content}</div>}
        <div className="space-y-4">
          {(course.questions || []).map((q, i) => (
            <div key={q.id} className="border border-slate-800 rounded-lg p-3">
              <div className="text-sm font-semibold mb-2">{i + 1}. {q.question}</div>
              <div className="space-y-1">
                {q.options.map((o, j) => (
                  <label key={`${q.id}-o-${j}`} className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer p-2 rounded hover:bg-[#0b0d12]">
                    <input type="radio" name={q.id} checked={answers[q.id] === j} onChange={() => setAnswers({ ...answers, [q.id]: j })} className="accent-[color:var(--accent)]" /> {o}
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
        {result && <div className={`mt-4 p-3 rounded-lg text-center font-bold ${result.status === "passed" ? "bg-[#ff000022] text-[color:var(--accent)]" : "bg-[#ff3b5c20] text-[#ff3b5c]"}`}>{result.score}% — {result.status.toUpperCase()}</div>}
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-400">Close</button>
          {!result && <button type="submit" data-testid="course-submit" className="px-4 py-2 rounded-lg text-black font-bold text-sm" style={{ background: "var(--accent)" }}>Submit</button>}
        </div>
      </form>
    </div>
  );
}

function NewCourse({ onClose, onDone }) {
  const [f, setF] = useState({ title: "", description: "", content: "", questionsText: "", pass_threshold: 70, max_attempts: 3 });
  const parseQuestions = () => {
    // Format: Q: question | A: opt1 | opt2 | *correctOpt | opt3
    return f.questionsText.split(/\n\s*\n/).map((block, i) => {
      const parts = block.split("|").map((s) => s.trim()).filter(Boolean);
      const question = parts[0]?.replace(/^Q:\s*/i, "") || "";
      if (!question) return null;
      const opts = parts.slice(1);
      let correct = opts.findIndex((o) => o.startsWith("*"));
      if (correct < 0) correct = 0;
      const cleanOpts = opts.map((o) => o.replace(/^\*/, ""));
      return { id: `q${i}`, question, options: cleanOpts, correct };
    }).filter(Boolean);
  };
  const submit = async (e) => { e.preventDefault(); await api.post("/courses", { title: f.title, description: f.description, content: f.content, pass_threshold: Number(f.pass_threshold), max_attempts: Number(f.max_attempts), questions: parseQuestions() }); onDone(); toast.success("Course created"); };
  const inp = "w-full bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 py-2 outline-none focus:border-[color:var(--accent)] text-sm";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <form onSubmit={submit} className="card-surface p-6 w-full max-w-lg max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="course-modal">
        <h2 className="text-xl font-bold mb-4">New course</h2>
        <label className="block mb-3"><div className="text-xs uppercase text-slate-500 mb-1">Title *</div><input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="course-title" className={inp} /></label>
        <label className="block mb-3"><div className="text-xs uppercase text-slate-500 mb-1">Description</div><input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="course-desc" className={inp} /></label>
        <label className="block mb-3"><div className="text-xs uppercase text-slate-500 mb-1">Content / lesson</div><textarea rows={4} value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} data-testid="course-content" className={inp + " resize-none"} /></label>
        <label className="block mb-3">
          <div className="text-xs uppercase text-slate-500 mb-1">Quiz (one per block, blank line between)</div>
          <textarea rows={6} value={f.questionsText} onChange={(e) => setF({ ...f, questionsText: e.target.value })} data-testid="course-qs" className={inp + " resize-none font-mono text-xs"} placeholder={`Q: What is our max response time? | 1h | 12h | *24h | 3 days\n\nQ: Ban appeals go where? | DMs | *#appeals | #general`} />
          <div className="text-[10px] text-slate-500 mt-1">Prefix the correct option with an asterisk (*)</div>
        </label>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <label className="block"><div className="text-xs uppercase text-slate-500 mb-1">Pass threshold %</div><input type="number" min={0} max={100} value={f.pass_threshold} onChange={(e) => setF({ ...f, pass_threshold: e.target.value })} data-testid="course-threshold" className={inp} /></label>
          <label className="block"><div className="text-xs uppercase text-slate-500 mb-1">Max attempts</div><input type="number" min={1} max={20} value={f.max_attempts} onChange={(e) => setF({ ...f, max_attempts: e.target.value })} data-testid="course-max-attempts" className={inp} /></label>
        </div>
        <div className="flex justify-end gap-2 mt-4"><button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-400">Cancel</button><button type="submit" data-testid="course-submit-new" className="px-4 py-2 rounded-lg text-black font-bold text-sm" style={{ background: "var(--accent)" }}>Create</button></div>
      </form>
    </div>
  );
}
